
import { Card, CardContent } from "@/components/ui/card";
import { DataOneUtils } from "./utils";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface VehicleImagesProps {
  data?: any;
  selectedStyle?: any;
  selectedStyleIndex?: number;
}

export const VehicleImages = ({ data, selectedStyle, selectedStyleIndex }: VehicleImagesProps) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  
  // Extract vehicle images based on selection or from data if not directly provided
  let vehicleImages = [];
  
  if (selectedStyle) {
    // Try to get images directly from the selected style
    const images = selectedStyle.images?.image || selectedStyle.style_image_data?.vehicle_images || [];
    vehicleImages = Array.isArray(images) ? images : [images].filter(Boolean);
  } else {
    // Fall back to utility function for getting images from overall data
    vehicleImages = DataOneUtils.extractVehicleImages(data) || [];
  }
  
  if (!vehicleImages || vehicleImages.length === 0) {
    return (
      <div className="text-gray-500">No vehicle images available</div>
    );
  }

  // Handle image click for full view
  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };
  
  // Get unique angle names to categorize images
  const uniqueAngles = Array.from(new Set(vehicleImages
    .map(img => img.angle || img.type || 'Unknown')
    .filter(Boolean)));
  
  return (
    <div className="space-y-6">
      {uniqueAngles.length > 0 ? (
        uniqueAngles.map(angle => {
          const angleImages = vehicleImages.filter(img => 
            (img.angle === angle || img.type === angle)
          );
          
          return (
            <div key={angle} className="mb-8">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                {DataOneUtils.formatAngleName(angle)}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {angleImages.map((image, index) => {
                  const imageUrl = image.url || image.image_url || '';
                  if (!imageUrl) return null;
                  
                  return (
                    <Card key={index} className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleImageClick(imageUrl)}>
                      <CardContent className="p-0">
                        <img 
                          src={imageUrl} 
                          alt={`${angle} view`} 
                          className="w-full h-auto object-cover"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = '/placeholder.svg';
                            e.currentTarget.classList.add('opacity-60');
                          }}
                        />
                        <div className="p-2">
                          <p className="text-xs text-gray-500">
                            {image.color || image.description || `Image ${index + 1}`}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })
      ) : (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>No categorized images available</AlertDescription>
        </Alert>
      )}
      
      {/* Full size image modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}>
          <div className="max-w-4xl max-h-screen relative">
            <Button 
              className="absolute top-2 right-2 bg-black bg-opacity-50 hover:bg-opacity-70"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(null);
              }}
            >
              Close
            </Button>
            <img 
              src={selectedImage} 
              alt="Vehicle" 
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleImages;
